import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check, Loader2, ArrowRight, ShieldCheck, ChevronDown, MessageCircle,
  MapPin, Video, Star, Sparkles, Instagram,
} from "lucide-react";
import axios from "axios";
import SEOHead from "@/components/site/SEOHead";
import FAQSection from "@/components/site/FAQSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- Configuration --------------------------------------------------------
// WhatsApp business EM Group Maurice
const WHATSAPP_NUMBER = "23058535782";
const WHATSAPP_MESSAGE = encodeURIComponent(
  "Bonjour EM Group, je suis intéressé(e) par votre offre site web à Maurice. Pouvez-vous m'en dire plus ?"
);
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;

// Instagram DM EM Group (@emgroup.digital)
const INSTAGRAM_USERNAME = "emgroup.digital";
const INSTAGRAM_DM_URL = `https://ig.me/m/${INSTAGRAM_USERNAME}`;

// --- SEO -------------------------------------------------------------------
const CANONICAL = "https://emgroupdigital.fr/site-location-maurice";
const TITLE = "Création site web & Gestion Réseaux Sociaux à Maurice — EM Group";
const DESCRIPTION =
  "Agence digitale basée à Maurice. Création de site web professionnel, gestion réseaux sociaux et campagnes Meta. Devis gratuit sous 24h.";
const KEYWORDS =
  "création site web Maurice, agence digitale Maurice, gestion réseaux sociaux Maurice, site web professionnel Maurice, agence web Maurice, campagnes Meta Maurice, community management Maurice, EM Group Maurice";

const LOCAL_BUSINESS = {
  name: "EM Group Maurice",
  description:
    "Agence digitale basée à l'île Maurice : création de site web professionnel, gestion des réseaux sociaux et campagnes Meta Ads pour les TPE/PME mauriciennes. Nom de domaine .mu, hébergement et accompagnement inclus.",
  email: "contact@emgroupdigital.fr",
  telephone: "+23058535782",
  url: CANONICAL,
  image: "https://emgroupdigital.fr/brand/logo-em-group.png",
  areaServed: ["Île Maurice", "Mauritius", "Port Louis", "Curepipe", "Grand Baie", "Ebène"],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Port Louis",
    addressRegion: "Île Maurice",
    addressCountry: "MU",
  },
};

const SERVICE_SCHEMA = {
  name: "Agence digitale à Maurice — Création site web, gestion réseaux sociaux et campagnes Meta",
  serviceType: "Création site web, gestion réseaux sociaux, campagnes Meta Ads",
  description:
    "Agence digitale à Maurice : création de site web professionnel avec nom de domaine .mu, gestion des réseaux sociaux (Instagram, Facebook, TikTok) et campagnes Meta Ads pour attirer des clients locaux. Formules Location, Achat et Accompagnement complet.",
  areaServed: "Île Maurice",
  price: "3500",
  priceCurrency: "MUR",
  priceDescription: "À partir de 3 500 Rs/mois — création, domaine .mu, hébergement et maintenance inclus.",
};

const FAQ_ITEMS = [
  {
    q: "Combien coûte un site web à Maurice ?",
    a: "Trois formules au choix : Location à partir de 3 500 Rs/mois (4 500 Rs les 3 premiers mois pour couvrir les frais de création), Achat à partir de 60 000 Rs (vous êtes 100% propriétaire du site), ou Accompagnement complet sur devis (site + branding + réseaux sociaux). Tous nos tarifs sont en roupies mauriciennes, sans frais cachés.",
  },
  {
    q: "EM Group est-il basé à Maurice ?",
    a: "Oui, EM Group dispose d'une présence locale à l'île Maurice. Vous nous joignez directement par WhatsApp au +230 5853 5782 ou par DM Instagram @emgroup.digital. Nous accompagnons les TPE/PME mauriciennes avec un nom de domaine .mu, un email professionnel et un site responsive livré clé en main.",
  },
  {
    q: "Quelle est la différence entre location et achat ?",
    a: "La location (3 500 Rs/mois) inclut tout : création, nom de domaine .mu, hébergement, maintenance et support — sans gros chèque au démarrage. L'achat (à partir de 60 000 Rs) vous rend propriétaire à 100% du site et de son code source ; vous gérez ensuite l'hébergement vous-même ou via nous sur devis séparé.",
  },
  {
    q: "Quel est l'engagement pour la formule Location ?",
    a: "La formule Location est souscrite pour une durée minimale de 12 mois calendaires à compter de la mise en ligne du site. La grille tarifaire couvre deux phases : les 3 premiers mois à 4 500 Rs/mois (période de création) puis les 9 mois suivants à 3 500 Rs/mois (période d'abonnement incluant hébergement, nom de domaine, maintenance et support). Passée cette période minimum de 12 mois, l'abonnement est résiliable à tout moment avec un préavis de 30 jours calendaires par email à contact@emgroupdigital.fr. Cet engagement garantit une prestation pérenne et permet d'amortir les frais de création sans avance initiale.",
  },
  {
    q: "Combien de temps faut-il pour créer mon site ?",
    a: "En général, votre site est en ligne en 7 à 14 jours selon la formule choisie et la rapidité de fourniture des contenus (textes, photos, logo). Un premier échange de cadrage nous permet de valider ensemble le planning précis dès la signature du devis.",
  },
  {
    q: "Que se passe-t-il si je veux arrêter la location ?",
    a: "Votre engagement couvre 12 mois minimum à compter de la mise en ligne du site. Passée cette période minimum, vous pouvez résilier à tout moment avec un préavis de 30 jours calendaires, par simple email à contact@emgroupdigital.fr. Le site reste notre propriété, mais l'intégralité de vos contenus (textes, images, données) vous est restituée. Un rachat du site en fin de contrat est également possible sur devis.",
  },
];

// Conversion tracking (reuse the Lead event already defined globally)
function fireLeadConversion(value = 30, currency = "EUR") {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "conversion", {
      send_to: "AW-18201979242/kUtFCJzRrrccEOrSsOdD",
      value, currency,
    });
  }
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "Lead", {
      content_name: "Site Location Maurice",
      value, currency,
    });
  }
}

// --- Offers ---------------------------------------------------------------
const OFFERS = [
  {
    key: "location",
    badge: "Choisie par 8 clients sur 10",
    name: "Location",
    price: "À partir de 3 500 Rs/mois",
    priceSub: "4 500 Rs/mois les 3 premiers mois, le temps de couvrir les frais de création.",
    pitch: "Sans gros chèque au démarrage, 12 mois minimum d'engagement.",
    fineprint: "Tarif de base pour un site jusqu'à 5 pages avec photos fournies par vos soins. Devis ajusté selon le nombre de pages, contenu et besoins spécifiques de votre projet.",
    includes: [
      "Création du site (jusqu'à 5 pages en base)",
      "Nom de domaine .mu (1ʳᵉ année incluse)",
      "Hébergement professionnel",
      "Email professionnel inclus (via Zoho Mail)",
      "Formulaire de contact",
      "Design responsive (mobile, tablette, desktop)",
      "Maintenance de base",
      "Support",
    ],
    highlight: true,
  },
  {
    key: "achat",
    name: "Achat",
    price: "À partir de 60 000 Rs",
    priceSub: "Devenez propriétaire de votre site.",
    pitch: "Site livré clé en main, vous en êtes 100% propriétaire.",
    fineprint: "Tarif de base, devis ajusté selon le nombre de pages, contenu et besoins spécifiques de votre projet.",
    includes: [
      "Création complète du site",
      "Nom de domaine 1ʳᵉ année incluse",
      "Site livré clé en main",
    ],
  },
  {
    key: "accompagnement",
    name: "Accompagnement complet",
    price: "Sur devis personnalisé",
    priceSub: "Site + branding + réseaux sociaux.",
    pitch: "L'écosystème digital complet de votre marque.",
    fineprint: "Forfait sur-mesure adapté à vos objectifs et au volume de production souhaité.",
    includes: [
      "Création du site web",
      "Identité visuelle / branding (logo, charte, visuels)",
      "Gestion des réseaux sociaux",
      "Stratégie digitale globale",
    ],
  },
];

function OfferCard({ offer, isOpen, onToggle, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 + index * 0.08 }}
      data-testid={`offer-${offer.key}`}
      className={`relative rounded-3xl border backdrop-blur-xl transition-all ${
        offer.highlight
          ? "border-[#E52431]/40 bg-gradient-to-br from-[#E52431]/[0.08] via-white/[0.03] to-transparent shadow-[0_0_60px_-15px_rgba(229,36,49,0.4)]"
          : "border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent hover:border-white/20"
      }`}
    >
      {offer.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-[#E52431] to-[#FF6A75] text-white text-[10px] uppercase tracking-[0.15em] font-bold shadow-lg shadow-[#E52431]/30">
          <Sparkles size={10} className="inline -mt-0.5 mr-1" />
          {offer.badge}
        </div>
      )}
      <button
        type="button"
        onClick={onToggle}
        data-testid={`offer-toggle-${offer.key}`}
        className="w-full text-left px-6 py-6 md:px-7 md:py-7"
        aria-expanded={isOpen}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.15em] text-white/45 font-semibold">— Offre {index + 1}</div>
            <h3 className="mt-1.5 font-outfit text-2xl md:text-3xl font-light tracking-tight">{offer.name}</h3>
            <div className={`mt-3 text-xl md:text-2xl font-medium ${offer.highlight ? "text-transparent bg-clip-text bg-gradient-to-r from-[#E52431] to-[#FF6A75]" : "text-white"}`}>
              {offer.price}
            </div>
            <div className="text-xs text-white/55 mt-1.5 leading-relaxed">{offer.priceSub}</div>
            <p className="text-sm text-white/75 mt-3 leading-relaxed">{offer.pitch}</p>
          </div>
          <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center border transition ${
            isOpen
              ? "bg-[#E52431]/15 border-[#E52431]/40 text-[#FFB4BB] rotate-180"
              : "bg-white/5 border-white/10 text-white/60"
          }`}>
            <ChevronDown size={16} />
          </div>
        </div>
        <div className="mt-4 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.12em] font-semibold text-white/55 hover:text-white transition">
          {isOpen ? "Masquer le détail" : "Voir le détail"} <ChevronDown size={11} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 md:px-7 md:pb-7 border-t border-white/5 pt-5">
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/45 font-semibold mb-3">Ce qui est inclus</div>
              <ul className="space-y-2.5">
                {offer.includes.map((line) => (
                  <li key={line} className="flex items-start gap-3 text-sm text-white/85">
                    <div className="mt-0.5 w-4 h-4 shrink-0 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                      <Check size={10} className="text-emerald-300" />
                    </div>
                    <span className="leading-relaxed">{line}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-[11px] leading-relaxed text-white/40 italic border-l-2 border-white/10 pl-3">
                {offer.fineprint}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// --- CGV — toujours en FR ------------------------------------------------
const CGV_ARTICLES = [
  { n: 1, title: "Objet", body: "Les présentes Conditions Générales de Vente (CGV) régissent la formule Location de site web proposée par EM Group Digital (« EM Group ») aux entreprises basées à l'île Maurice.\n\nL'offre concerne la création, l'hébergement et la maintenance d'un site internet professionnel avec nom de domaine .mu." },
  { n: 2, title: "Formule Location", body: "La formule Location inclut :\n\n• Création d'un site web professionnel jusqu'à 5 pages en base\n• Nom de domaine .mu (première année incluse)\n• Hébergement professionnel sécurisé\n• Certificat SSL et design responsive (mobile / tablette / desktop)\n• Maintenance technique et mises à jour de sécurité\n• Support par email et WhatsApp\n\nLes prestations complémentaires (pages additionnelles, refonte majeure, e-commerce, contenus rédactionnels étendus) font l'objet d'un devis séparé." },
  { n: 3, title: "Tarifs et facturation", body: "Le tarif de la formule Location est de :\n\n• 4 500 Rs/mois TTC pendant les 3 premiers mois (phase de création)\n• 3 500 Rs/mois TTC à compter du 4ᵉ mois (phase d'abonnement)\n\nLes tarifs indiqués sont en roupies mauriciennes (MUR) et s'entendent toutes taxes comprises. Les factures sont émises mensuellement et payables à réception." },
  { n: 4, title: "Engagement contractuel", body: "La formule Location est souscrite pour une durée minimale de 12 mois calendaires à compter de la mise en ligne du site.\n\nCet engagement de 12 mois garantit une prestation pérenne et permet la mise en place de la formule Location sans avance de frais de production. Il couvre l'ensemble de la période contractuelle : phase de création (mois 1 à 3, facturés 4 500 Rs/mois) et phase d'abonnement (mois 4 à 12, facturés 3 500 Rs/mois)." },
  { n: 5, title: "Résiliation", body: "Aucune résiliation anticipée n'est possible pendant la période d'engagement minimum de 12 mois, sauf cas de force majeure ou manquement grave d'EM Group dûment constaté.\n\nÀ l'issue de cette période minimum de 12 mois, le contrat se poursuit tacitement au tarif mensuel de 3 500 Rs/mois et devient résiliable à tout moment par le client avec un préavis de 30 jours calendaires, notifié par email à contact@emgroupdigital.fr.\n\nEn cas de résiliation anticipée à l'initiative du client hors des cas prévus ci-dessus, l'intégralité des mensualités restantes jusqu'à la fin de la période d'engagement reste due." },
  { n: 6, title: "Propriété du site", body: "Dans le cadre de la formule Location, EM Group demeure propriétaire du site (design, code source, développements techniques) pendant toute la durée du contrat. Le client bénéficie d'un droit d'usage exclusif du site dans le cadre de son activité professionnelle.\n\nLe client conserve la propriété pleine et entière de sa marque, de son logo et des contenus qu'il fournit (textes, photos, vidéos).\n\nUn rachat du site en fin de contrat est possible sur devis." },
  { n: 7, title: "Contenu et validation", body: "Le client fournit à EM Group les éléments nécessaires à la création du site (textes, photos, logo, coordonnées, mentions légales). En l'absence de photos fournies, EM Group utilise des visuels libres de droits.\n\nLe site est soumis à validation du client avant mise en ligne. Le client dispose de 7 jours ouvrés pour valider ou demander des modifications. Passé ce délai sans retour, le site est considéré validé et mis en ligne." },
  { n: 8, title: "Maintenance et disponibilité", body: "EM Group assure la maintenance technique du site (mises à jour de sécurité, corrections de bugs, sauvegardes régulières) pendant toute la durée du contrat.\n\nEM Group s'engage à un taux de disponibilité du site de 99% en moyenne annuelle, hors interventions planifiées et cas de force majeure. Les interruptions techniques prolongées sont notifiées au client dans les meilleurs délais." },
  { n: 9, title: "Modifications du site", body: "Les modifications mineures (mise à jour de textes, ajout d'images, changement de coordonnées) sont incluses dans la formule Location dans la limite de 2 demandes par mois.\n\nLes modifications majeures (nouvelle page, refonte graphique, ajout de fonctionnalités, module e-commerce) font l'objet d'un devis séparé et acceptation préalable du client." },
  { n: 10, title: "Nom de domaine et hébergement", body: "Le nom de domaine .mu est enregistré au nom du client, avec EM Group comme prestataire technique. En cas de résiliation du contrat, le client peut demander le transfert du nom de domaine à ses frais.\n\nL'hébergement est fourni par EM Group sur des serveurs professionnels sécurisés. Les données du site sont sauvegardées quotidiennement." },
  { n: 11, title: "Paiement", body: "Le paiement s'effectue par virement bancaire ou tout autre mode convenu entre les parties. Aucun prélèvement automatique n'est mis en place pour les clients mauriciens.\n\nEn cas de retard de paiement supérieur à 15 jours après relance, EM Group se réserve le droit de suspendre l'accès au site jusqu'à régularisation." },
  { n: 12, title: "Droit applicable", body: "Les présentes CGV sont soumises au droit français applicable aux prestations fournies par EM Group Digital. Pour les clients basés à Maurice, les parties s'engagent à rechercher une solution amiable avant tout recours judiciaire.\n\nÀ défaut d'accord, les tribunaux compétents seront ceux du siège social d'EM Group Digital." },
];

// --- A/B Test — CTA Formulaire --------------------------------------------
const AB_TEST_KEY = "em_ab_maurice_cta";
const AB_VARIANTS = {
  a: "Demander mon devis gratuit",
  b: "Recevoir mon devis sous 24h",
};

function getOrAssignAbVariant() {
  if (typeof window === "undefined") return "a";
  try {
    let v = window.localStorage.getItem(AB_TEST_KEY);
    if (v !== "a" && v !== "b") {
      v = Math.random() < 0.5 ? "a" : "b";
      window.localStorage.setItem(AB_TEST_KEY, v);
    }
    return v;
  } catch {
    return "a";
  }
}

// --- Page -----------------------------------------------------------------
export default function SiteLocationMaurice() {
  const [openOffer, setOpenOffer] = useState("location");
  const [openCgv, setOpenCgv] = useState(-1);
  const [form, setForm] = useState({ name: "", phone: "", email: "", offer: "location", has_website: "", budget: "", timeline: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [abVariant, setAbVariant] = useState("a");

  useEffect(() => {
    // A/B test assignation + view tracking
    const v = getOrAssignAbVariant();
    setAbVariant(v);
    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("trackCustom", "SiteLocationMauriceCTAView", { variant: v });
    }
    // Persist the impression server-side (fire-and-forget) so admin conversion rate is accurate
    axios
      .post(`${API}/leads/site-location-maurice/ab-view`, {
        experiment: "site-location-maurice-cta",
        variant: v,
      })
      .catch(() => {
        /* silent — impression tracking is best-effort */
      });
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.has_website || !form.budget || !form.timeline) {
      setError("Merci de remplir tous les champs.");
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      await axios.post(`${API}/leads/site-location-maurice`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        offer: form.offer,
        has_website: form.has_website,
        budget: form.budget,
        timeline: form.timeline,
        ab_variant: abVariant,
        utm_source: params.get("utm_source") || "",
        utm_campaign: params.get("utm_campaign") || "",
      });
      // A/B conversion tracking
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("trackCustom", "SiteLocationMauriceCTAConversion", { variant: abVariant });
      }
      fireLeadConversion(30);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue. Réessayez ou écrivez-nous à contact@emgroupdigital.fr");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0E] text-white relative overflow-hidden" data-testid="site-location-maurice-page">
      <SEOHead
        title={TITLE}
        description={DESCRIPTION}
        keywords={KEYWORDS}
        canonical={CANONICAL}
        ogImage="https://emgroupdigital.fr/brand/logo-em-group.png"
        locale="fr_MU"
        htmlLang="fr"
        robots="index, follow"
        localBusiness={LOCAL_BUSINESS}
        serviceSchema={SERVICE_SCHEMA}
        faqSchema={FAQ_ITEMS}
      />
      {/* Ambient gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-[#E52431]/20 blur-[120px]" />
        <div className="absolute top-1/3 -right-40 w-[420px] h-[420px] rounded-full bg-[#FF6A75]/10 blur-[120px]" />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 md:px-10 pt-6 md:pt-8">
        <Link to="/" data-testid="site-location-mu-logo" className="inline-flex items-center gap-2 group">
          <img
            src="/brand/logo-em-group.png"
            alt="EM Group — Agence digitale à Maurice : création site web et gestion réseaux sociaux"
            width="40"
            height="40"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="w-10 h-10 rounded-full"
          />
          <span className="font-outfit text-base font-medium tracking-tight text-white/90 group-hover:text-white transition">
            EM Group
          </span>
        </Link>
      </header>

      <main className="relative z-10 px-6 md:px-10 py-10 md:py-16 max-w-5xl mx-auto">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-[0.15em] text-white/65 font-semibold">
            <span className="text-base leading-none">🇲🇺</span> Île Maurice — Offre PME / TPE
          </div>

          <h1 className="mt-6 font-outfit text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
            Agence digitale à Maurice —{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E52431] to-[#FF6A75] font-medium">
              création site web
            </span>
            <br className="hidden md:block" /> &amp; gestion réseaux sociaux.
          </h1>

          <p className="mt-5 text-white/65 text-base md:text-lg leading-relaxed max-w-2xl">
            Site web professionnel à Maurice à partir de <strong className="text-white">3 500 Rs/mois</strong>, gestion des réseaux sociaux (Instagram, Facebook, TikTok) et campagnes Meta Ads pour attirer des clients locaux. Location, achat ou accompagnement complet — <strong className="text-white">devis gratuit sous 24h</strong>.
          </p>
        </motion.div>

        {/* Offers */}
        <section className="mt-12 md:mt-16 space-y-4" aria-label="Nos formules" data-testid="offers-section">
          {OFFERS.map((o, i) => (
            <OfferCard
              key={o.key}
              offer={o}
              isOpen={openOffer === o.key}
              onToggle={() => setOpenOffer(openOffer === o.key ? "" : o.key)}
              index={i}
            />
          ))}
        </section>

        {/* Differentiator */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mt-16 md:mt-20 rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.05] via-white/[0.02] to-transparent p-6 md:p-8 backdrop-blur-xl"
          data-testid="diff-section"
        >
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-12 h-12 rounded-2xl bg-[#E52431]/15 border border-[#E52431]/30 flex items-center justify-center">
              <MapPin size={20} className="text-[#FFB4BB]" />
            </div>
            <div className="flex-1">
              <h2 className="font-outfit text-2xl md:text-3xl font-light tracking-tight">
                Agence <span className="font-medium">EM Group</span> basée à Maurice.
              </h2>
              <p className="mt-3 text-white/70 leading-relaxed">
                Un interlocuteur local, pas un service à distance. <strong className="text-white">Rendez-vous possibles en présentiel ou en visio</strong> selon votre préférence.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/75">
                  <MapPin size={11} className="text-[#FFB4BB]" /> RDV en présentiel
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/75">
                  <Video size={11} className="text-[#FFB4BB]" /> Visio si vous préférez
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/75">
                  <Star size={11} className="fill-amber-400 text-amber-400" /> Français · Créole · Anglais
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Contact CTAs */}
        <section className="mt-16 md:mt-20" data-testid="contact-section">
          <div className="text-center mb-8 md:mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[10px] uppercase tracking-[0.18em] text-white/55 font-semibold">
              <Sparkles size={11} className="text-[#FFB4BB]" /> On échange ?
            </div>
            <h2 className="mt-5 font-outfit text-3xl md:text-4xl font-light tracking-tight">
              Discutons de votre projet — <br className="hidden sm:block" />
              <span className="text-white/55">3 façons de nous contacter.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* Left col — Quick contact cards stacked */}
            <div className="flex flex-col gap-5">
              {/* Option A — WhatsApp */}
              <motion.a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => fireLeadConversion(20)}
                data-testid="whatsapp-cta"
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55 }}
                className="group flex-1 rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.08] via-emerald-500/[0.03] to-transparent p-6 md:p-7 hover:border-emerald-400/60 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-emerald-500/5"
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                    <MessageCircle size={22} className="text-emerald-300" />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-300/80 font-semibold">Le plus rapide</div>
                </div>
                <h3 className="mt-4 font-outfit text-xl md:text-2xl font-light tracking-tight">WhatsApp</h3>
                <p className="mt-1.5 text-sm text-white/65 leading-relaxed">
                  Message direct, réponse rapide. Idéal pour une question ou cadrer le projet.
                </p>
                <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-emerald-300 group-hover:text-emerald-200">
                  Ouvrir WhatsApp <ArrowRight size={14} className="group-hover:translate-x-0.5 transition" />
                </div>
              </motion.a>

              {/* Option B — Instagram DM */}
              <motion.a
                href={INSTAGRAM_DM_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => fireLeadConversion(20)}
                data-testid="instagram-cta"
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: 0.05 }}
                className="group flex-1 rounded-3xl border border-pink-500/30 bg-gradient-to-br from-fuchsia-500/[0.10] via-pink-500/[0.05] to-amber-500/[0.04] p-6 md:p-7 hover:border-fuchsia-400/60 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-fuchsia-500/5"
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-fuchsia-500/30 to-amber-500/30 border border-pink-400/30 flex items-center justify-center">
                    <Instagram size={22} className="text-pink-200" />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-pink-200/80 font-semibold">@emgroup.digital</div>
                </div>
                <h3 className="mt-4 font-outfit text-xl md:text-2xl font-light tracking-tight">Instagram DM</h3>
                <p className="mt-1.5 text-sm text-white/65 leading-relaxed">
                  Glissez-nous un message sur Insta. Pratique si vous nous suivez déjà.
                </p>
                <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-pink-200 group-hover:text-pink-100">
                  Ouvrir le DM <ArrowRight size={14} className="group-hover:translate-x-0.5 transition" />
                </div>
              </motion.a>
            </div>

            {/* Option C — Form */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.1 }}
              className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.04] via-white/[0.02] to-transparent p-6 md:p-7"
            >
              {!submitted ? (
                <form onSubmit={onSubmit} data-testid="contact-form">
                  <div className="flex items-center justify-between">
                    <div className="w-14 h-14 rounded-2xl bg-[#E52431]/15 border border-[#E52431]/30 flex items-center justify-center">
                      <Sparkles size={26} className="text-[#FFB4BB]" />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-white/45 font-semibold">Devis détaillé</div>
                  </div>
                  <h3 className="mt-5 font-outfit text-2xl font-light tracking-tight">Demande de devis</h3>
                  <p className="mt-2 text-sm text-white/65">On vous rappelle sous 24h.</p>

                  <div className="mt-5 space-y-3">
                    <input
                      type="text" placeholder="Nom complet" autoComplete="name"
                      value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="form-name"
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:border-[#E52431]/60 outline-none transition"
                      required
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="tel" placeholder="Téléphone" autoComplete="tel"
                        value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        data-testid="form-phone"
                        className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:border-[#E52431]/60 outline-none transition"
                        required
                      />
                      <input
                        type="email" placeholder="Email" autoComplete="email"
                        value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="form-email"
                        className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:border-[#E52431]/60 outline-none transition"
                        required
                      />
                    </div>
                    <select
                      value={form.offer}
                      onChange={(e) => setForm({ ...form, offer: e.target.value })}
                      data-testid="form-offer"
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-[#E52431]/60 outline-none transition"
                    >
                      <option value="location">Quelle offre ? — Location</option>
                      <option value="achat">Quelle offre ? — Achat</option>
                      <option value="accompagnement">Quelle offre ? — Accompagnement complet</option>
                    </select>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.1em] text-white/55 font-medium mb-1.5">Avez-vous déjà un site web ?</div>
                      <div className="grid grid-cols-2 gap-2">
                        {[{ value: "yes", label: "Oui" }, { value: "no", label: "Non" }].map((opt) => {
                          const active = form.has_website === opt.value;
                          return (
                            <button
                              key={opt.value} type="button"
                              onClick={() => setForm({ ...form, has_website: opt.value })}
                              data-testid={`form-has-website-${opt.value}`}
                              className={`px-3 py-2.5 rounded-xl text-sm font-medium transition border ${
                                active
                                  ? "bg-[#E52431]/15 border-[#E52431]/50 text-white"
                                  : "bg-black/30 border-white/10 text-white/65 hover:bg-black/40"
                              }`}
                            >{opt.label}</button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.1em] text-white/55 font-medium mb-1.5">Quel est votre budget mensuel ?</div>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: "lt5000", label: "< 5 000 Rs" },
                          { value: "5000_10000", label: "5–10 000 Rs" },
                          { value: "gt10000", label: "> 10 000 Rs" },
                        ].map((opt) => {
                          const active = form.budget === opt.value;
                          return (
                            <button
                              key={opt.value} type="button"
                              onClick={() => setForm({ ...form, budget: opt.value })}
                              data-testid={`form-budget-${opt.value}`}
                              className={`px-2.5 py-2.5 rounded-xl text-xs md:text-sm font-medium transition border ${
                                active
                                  ? "bg-[#E52431]/15 border-[#E52431]/50 text-white"
                                  : "bg-black/30 border-white/10 text-white/65 hover:bg-black/40"
                              }`}
                            >{opt.label}</button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.1em] text-white/55 font-medium mb-1.5">Quel est votre délai ?</div>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: "urgent", label: "Urgent (< 1 mois)" },
                          { value: "1_3_months", label: "1 à 3 mois" },
                          { value: "no_deadline", label: "Pas de délai" },
                        ].map((opt) => {
                          const active = form.timeline === opt.value;
                          return (
                            <button
                              key={opt.value} type="button"
                              onClick={() => setForm({ ...form, timeline: opt.value })}
                              data-testid={`form-timeline-${opt.value}`}
                              className={`px-2.5 py-2.5 rounded-xl text-xs md:text-sm font-medium transition border ${
                                active
                                  ? "bg-[#E52431]/15 border-[#E52431]/50 text-white"
                                  : "bg-black/30 border-white/10 text-white/65 hover:bg-black/40"
                              }`}
                            >{opt.label}</button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="mt-4 p-3 rounded-xl bg-[#E52431]/10 border border-[#E52431]/30 text-sm text-[#FFB4BB]" data-testid="form-error">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit" disabled={loading}
                    data-testid="form-submit"
                    data-ab-variant={abVariant}
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#E52431] to-[#FF6A75] text-white font-semibold px-5 py-3.5 text-xs uppercase tracking-[0.12em] hover:shadow-[0_0_30px_rgba(229,36,49,0.45)] transition disabled:opacity-60"
                  >
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                    {loading ? "Envoi…" : AB_VARIANTS[abVariant]}
                  </button>
                </form>
              ) : (
                <div className="text-center py-6" data-testid="form-success">
                  <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 mx-auto flex items-center justify-center">
                    <Check size={28} className="text-emerald-300" />
                  </div>
                  <h3 className="mt-5 font-outfit text-2xl font-light tracking-tight">Demande bien reçue.</h3>
                  <p className="mt-2 text-white/65 text-sm leading-relaxed">
                    On vous rappelle <strong className="text-white">sous 24h</strong> pour discuter.
                  </p>
                </div>
              )}
            </motion.div>
          </div>

          <p className="mt-8 text-center text-sm text-white/55 leading-relaxed max-w-2xl mx-auto" data-testid="payment-note">
            <ShieldCheck size={13} className="inline -mt-0.5 mr-1.5 text-[#FFB4BB]" />
            On vous répond sous 24h pour discuter de votre projet et finaliser ensemble le mode de paiement qui vous convient (carte ou virement local).
          </p>
        </section>

        {/* SEO content section — services agence digitale Maurice */}
        <section className="mt-16 md:mt-20 space-y-6 max-w-3xl" aria-label="Services agence digitale Maurice" data-testid="seo-services-mu">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">— Nos expertises à Maurice</div>
          <h2 className="font-outfit text-2xl md:text-3xl font-light tracking-tight">
            Une <span className="text-white">agence digitale à Maurice</span> pour faire décoller votre présence en ligne.
          </h2>
          <p className="text-white/65 text-sm md:text-base leading-relaxed">
            EM Group est une agence digitale basée à Maurice qui accompagne les TPE/PME mauriciennes sur trois piliers complémentaires : la <strong className="text-white">création de site web Maurice</strong>, la <strong className="text-white">gestion des réseaux sociaux à Maurice</strong> (Instagram, Facebook, TikTok) et la mise en place de <strong className="text-white">campagnes Meta Ads</strong> ciblées pour attirer une clientèle locale qualifiée.
          </p>
          <div className="grid md:grid-cols-3 gap-4 pt-2">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="seo-card-siteweb">
              <div className="text-xs uppercase tracking-[0.14em] text-[#FFB4BB] font-semibold">Site web professionnel Maurice</div>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">
                Site web sur-mesure livré clé en main avec nom de domaine <strong className="text-white">.mu</strong>, hébergement sécurisé et maintenance inclus. À partir de 3 500 Rs/mois en location.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="seo-card-reseaux">
              <div className="text-xs uppercase tracking-[0.14em] text-[#FFB4BB] font-semibold">Gestion réseaux sociaux Maurice</div>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">
                Contenus Instagram, Facebook et TikTok pensés pour le marché mauricien : stratégie éditoriale, création visuelle, publication régulière et engagement communautaire.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="seo-card-meta">
              <div className="text-xs uppercase tracking-[0.14em] text-[#FFB4BB] font-semibold">Campagnes Meta Ads Maurice</div>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">
                Publicités Facebook &amp; Instagram géociblées sur l'île Maurice pour toucher vos clients potentiels dans les zones qui vous intéressent (Port Louis, Curepipe, Grand Baie, Ebène...).
              </p>
            </div>
          </div>
        </section>

        {/* FAQ structurée */}
        <FAQSection
          items={FAQ_ITEMS}
          testIdPrefix="site-location-maurice-faq"
          title="Questions fréquentes sur la création de site web à Maurice."
        />
      </main>

      {/* CGV — toujours en FR */}
      <section className="relative z-10 bg-[#06060A] border-t border-white/5 px-6 md:px-10 py-14" data-testid="cgv-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">— Cadre légal</div>
          <h2 className="mt-2 font-outfit text-2xl md:text-3xl font-light tracking-tight">
            Conditions Générales de Vente — Formule Location Maurice
          </h2>
          <p className="mt-3 text-sm text-white/55 leading-relaxed">
            Engagement 12 mois minimum d&apos;abonnement à compter de la mise en ligne. Résiliation avec préavis de 30 jours à l&apos;issue de la période minimum.
          </p>
          <div className="mt-6 rounded-3xl border border-white/8 bg-white/[0.015] divide-y divide-white/5 overflow-hidden">
            {CGV_ARTICLES.map((art, i) => {
              const isOpen = openCgv === i;
              return (
                <div key={art.n} data-testid={`cgv-article-${art.n}`}>
                  <button
                    type="button"
                    onClick={() => setOpenCgv(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    data-testid={`cgv-article-toggle-${art.n}`}
                    className="w-full text-left flex items-start gap-4 px-5 md:px-7 py-4 hover:bg-white/[0.015] transition"
                  >
                    <span className="shrink-0 mt-0.5 text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono">
                      Art. {art.n}
                    </span>
                    <span className="flex-1 text-sm md:text-base font-medium text-white/85">{art.title}</span>
                    <ChevronDown
                      size={14}
                      className={`shrink-0 mt-1 text-white/45 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-5 md:px-7 pb-5 text-xs md:text-sm text-white/65 leading-relaxed whitespace-pre-line">
                      {art.body}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <footer className="relative z-10 px-6 md:px-10 py-8 mt-10 text-[11px] text-white/35 text-center">
        © {new Date().getFullYear()} EM Group · <a href="/mentions-legales" className="hover:text-white/60 transition">Mentions légales</a> · <a href="/confidentialite" className="hover:text-white/60 transition">Confidentialité</a>
      </footer>
    </div>
  );
}
